/**
 * ReaderCreditContext - Global state for Reader Credit balance
 * 
 * CRITICAL: TRI-STATE BALANCE
 * - null = unknown/unhydrated (show "—")
 * - number = known (show actual value)
 * - error flag = fetch failed (show last known or "—")
 * 
 * NEVER default to 0 - that's a lie about user's actual balance.
 * 
 * REFRESH PATTERN: Pattern A (Use response balance)
 * - Summary endpoint returns authoritative balance + transactions
 * - Debit endpoint returns updated balance
 * - UI updates from response
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { BACKEND_URL as backendUrl } from '@/config/api';

const ReaderCreditContext = createContext(null);

export const useReaderCredits = () => {
  const context = useContext(ReaderCreditContext);
  if (!context) {
    throw new Error('useReaderCredits must be used within a ReaderCreditProvider');
  }
  return context;
};

export const ReaderCreditProvider = ({ children }) => {
  // TRI-STATE BALANCE: null = unknown, number = known
  // CRITICAL: Never set to 0 unless that's the true balance
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [transactions, setTransactions] = useState([]);
  
  // Track last known balance for error recovery
  const lastKnownBalanceRef = useRef(null);
  // Prevent concurrent fetches
  const fetchingRef = useRef(false);
  // Track if initial fetch has been done
  const initialFetchDoneRef = useRef(false);

  /**
   * Get auth token, user ID, and roles
   * Returns null if not authenticated
   * P0.5: Also checks if user has Reader role for RCM gating
   */
  const getAuthInfo = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.user_id || payload.sub;
      if (!userId) return null;
      
      // P0.5: Extract roles to determine if this is a Reader account
      const roles = payload.roles || [];
      
      // Check activeRole from sessionStorage - if user has explicitly selected 'reader' role, show credits
      const activeRole = sessionStorage.getItem('activeRole');
      
      // User is considered a Reader if:
      // 1. They have 'reader' role AND have explicitly selected it as active role, OR
      // 2. They ONLY have the 'reader' role (no other identity roles)
      const hasNonReaderRole = 
        roles.includes('admin') || 
        roles.includes('publisher') || 
        roles.includes('author') || 
        roles.includes('creator');
      
      const isReader = activeRole === 'reader' || !hasNonReaderRole;
      
      return { token, userId, isReader };
    } catch {
      return null;
    }
  }, []);

  /**
   * Fetch credits summary: balance + last N transactions
   * GET /api/reader/credits/summary?limit=N
   * 
   * This is the PRIMARY method - returns authoritative balance AND transactions
   * P0.5: Only fetches for Reader roles - non-Readers skip this entirely
   */
  const refreshCreditsSummary = useCallback(async (limit = 10) => {
    const auth = getAuthInfo();
    if (!auth) {
      console.log('[CREDITS] No auth, clearing state');
      setBalance(null);
      setTransactions([]);
      setLoading(false);
      return null;
    }
    
    // P0.5: Skip credit fetch for non-Reader roles
    // This prevents unnecessary API calls and ensures RCM logic is isolated
    if (!auth.isReader) {
      console.log('[CREDITS] Non-Reader role detected, skipping credit fetch');
      setBalance(null);
      setTransactions([]);
      setLoading(false);
      return null;
    }
    
    // Prevent concurrent fetches
    if (fetchingRef.current) {
      console.log('[CREDITS] Fetch already in progress, skipping');
      return { balance: lastKnownBalanceRef.current, transactions: [] };
    }
    
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      // backendUrl imported from centralized config as BACKEND_URL
      const response = await fetch(
        `${backendUrl}/api/reader/credits/summary?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${auth.token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('[CREDITS] Summary loaded: balance=', data.balance);
      
      // Update state with authoritative values
      const newBalance = typeof data.balance === 'number' ? data.balance : Number(data.balance);
      setBalance(newBalance);
      lastKnownBalanceRef.current = newBalance;
      setLastUpdated(data.as_of || new Date().toISOString());
      setTransactions(Array.isArray(data.transactions) ? data.transactions : []);
      setError(null);
      
      return data;
      
    } catch (err) {
      console.error('[CREDITS] Summary fetch error:', err);
      setError(err.message || 'Failed to load credits');
      // CRITICAL: Do NOT set balance to 0 on error
      // Keep showing last known balance
      return null;
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [getAuthInfo]);

  /**
   * Fetch balance only (legacy endpoint)
   */
  const fetchBalance = useCallback(async () => {
    return refreshCreditsSummary(10);
  }, [refreshCreditsSummary]);

  /**
   * Update balance from debit/credit response
   * Pattern A: Use response balance directly (immediate, authoritative)
   */
  const updateBalanceFromResponse = useCallback((newBalance) => {
    if (typeof newBalance === 'number' && newBalance >= 0) {
      console.log('[CREDITS] Balance updated from response:', newBalance);
      setBalance(newBalance);
      lastKnownBalanceRef.current = newBalance;
      setLastUpdated(new Date().toISOString());
      setError(null);
    }
  }, []);

  /**
   * Record a debit interaction
   * Called after successful debit-triggering action
   */
  const recordDebit = useCallback((debitResult) => {
    if (debitResult && typeof debitResult.balance === 'number') {
      updateBalanceFromResponse(debitResult.balance);
    } else {
      refreshCreditsSummary(10);
    }
  }, [updateBalanceFromResponse, refreshCreditsSummary]);

  /**
   * Optimistic debit - immediately update balance before backend confirms
   * Used for instant UI feedback when closing sessions
   * 
   * @param {number} estimatedDebit - Estimated credits to deduct
   * @returns {function} Rollback function to restore previous balance on error
   */
  const optimisticDebit = useCallback((estimatedDebit) => {
    if (typeof balance !== 'number' || typeof estimatedDebit !== 'number') {
      return () => {}; // No-op rollback
    }
    
    const previousBalance = balance;
    const newBalance = Math.max(0, balance - estimatedDebit);
    
    console.log('[CREDITS] Optimistic debit:', {
      previous: previousBalance,
      debit: estimatedDebit,
      new: newBalance
    });
    
    setBalance(newBalance);
    lastKnownBalanceRef.current = newBalance;
    setLastUpdated(new Date().toISOString());
    
    // Return rollback function
    return () => {
      console.log('[CREDITS] Rolling back optimistic debit to:', previousBalance);
      setBalance(previousBalance);
      lastKnownBalanceRef.current = previousBalance;
    };
  }, [balance]);

  // Initial load - fetch summary ONCE when auth becomes available
  useEffect(() => {
    // Check auth on mount and every few seconds
    const checkAndFetch = () => {
      const auth = getAuthInfo();
      if (auth && !initialFetchDoneRef.current && !fetchingRef.current) {
        console.log('[CREDITS] Auth found, fetching summary');
        initialFetchDoneRef.current = true;
        refreshCreditsSummary(10);
      } else if (!auth) {
        setLoading(false);
      }
    };
    
    // Check immediately
    checkAndFetch();
    
    // Also check after a short delay (auth might load async)
    const timeout = setTimeout(checkAndFetch, 500);
    const timeout2 = setTimeout(checkAndFetch, 1500);
    
    return () => {
      clearTimeout(timeout);
      clearTimeout(timeout2);
    };
  }, [getAuthInfo, refreshCreditsSummary]);

  // Listen for auth changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'accessToken') {
        console.log('[CREDITS] Token changed, refetching');
        lastKnownBalanceRef.current = null;
        initialFetchDoneRef.current = false;
        setBalance(null);
        setTransactions([]);
        const auth = getAuthInfo();
        if (auth) {
          initialFetchDoneRef.current = true;
          refreshCreditsSummary(10);
        }
      }
    };
    
    const handleAuthChange = () => {
      console.log('[CREDITS] Auth changed event');
      lastKnownBalanceRef.current = null;
      initialFetchDoneRef.current = false;
      setBalance(null);
      setTransactions([]);
      const auth = getAuthInfo();
      if (auth) {
        initialFetchDoneRef.current = true;
        refreshCreditsSummary(10);
      }
    };
    
    // Handle role switch events - when user switches to Reader role, fetch credits
    const handleRoleSwitch = (e) => {
      const newRole = e.detail?.role || sessionStorage.getItem('activeRole');
      console.log('[CREDITS] Role switch event, new role:', newRole);
      
      if (newRole === 'reader') {
        // User switched to Reader role - fetch credits
        lastKnownBalanceRef.current = null;
        initialFetchDoneRef.current = false;
        setBalance(null);
        setTransactions([]);
        const auth = getAuthInfo();
        if (auth && auth.isReader) {
          initialFetchDoneRef.current = true;
          refreshCreditsSummary(10);
        }
      } else {
        // User switched away from Reader - clear credits display
        setBalance(null);
        setTransactions([]);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-change', handleAuthChange);
    window.addEventListener('role-switch', handleRoleSwitch);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-change', handleAuthChange);
      window.removeEventListener('role-switch', handleRoleSwitch);
    };
  }, [getAuthInfo, refreshCreditsSummary]);

  const value = {
    // State
    balance,
    loading,
    error,
    lastUpdated,
    transactions,
    
    // Actions
    fetchBalance,              // Legacy single balance fetch
    refreshCreditsSummary,     // Full summary with transactions (preferred)
    updateBalanceFromResponse, // Update from API response
    recordDebit,               // After debit interaction
    optimisticDebit,           // Instant UI update before backend confirms
  };

  return (
    <ReaderCreditContext.Provider value={value}>
      {children}
    </ReaderCreditContext.Provider>
  );
};

export default ReaderCreditContext;
